// frontend/src/components/NextStepCard.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-02-12
// Updated:      2026-09-16
//
// Update (2026-09-01):
//   - Quote→Job launch now carries actual Quote line-item context.
//   - Adds a text Job Type hint without assuming Quote service_id == Job Type id.
//   - Quote and Opportunity provenance continue to be passed explicitly.
//
// Responsive pass (2026-09-16):
//   - Header status/reload controls wrap safely on narrow screens.
//   - Metadata values and long quote titles wrap instead of truncating/overflowing.
//   - Action buttons preserve their two-part labels without forcing horizontal overflow.
//   - Approver and paired action layouts collapse cleanly on mobile.
//   - No workflow, governance, quote transition, email, or Quote-to-Job logic changed.
//
// Description:
//   Reusable “Next step” workflow card for Quotes.
//   - Driven by GET /api/quotes/{id}/status/meta (DB governance authoritative)
//   - All editable users retain the normal submit/approval/send workflow.
//   - Admin/editor users additionally receive automatically detected administrative stage controls.
//   - Sent quotes show commercial actions: Accept Quote / Accept Option / Withdraw / Expire.
//   - Accepted quotes show operational actions: Create Job / Withdraw.
//   - Uses POST /api/quotes/{id}/status for direct status movement.
//   - Expired quotes can be reopened to Draft by an administrator; full validity/expiry redesign is deferred as TD-QUOTE-001.
//   - Can optionally self-load meta if meta not provided.
//
// IMPORTANT:
//   Theme aware: uses ThemeContext + standard button palettes (general/action).
//   No regressions: does not mutate QuoteCreateModal; can be mounted anywhere.

import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext";
import EmailMessageModal from "./EmailMessageModal.jsx";

const norm = (v) => String(v ?? "").trim();
const normStatus = (s) => norm(s).toLowerCase();

const ADMIN_STAGE_DESTINATIONS = [
  "draft",
  "submitted",
  "approved",
  "sent",
  "rejected",
  "expired",
  "withdrawn",
];

const toIntOrNull = (v) => {
  const s = norm(v);
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const hasAllowed = (meta, status) => {
  const a = safeArray(meta?.allowed_next_statuses);
  return a.map((x) => normStatus(x)).includes(normStatus(status));
};

const fmtStatus = (s) => {
  const v = normStatus(s);
  if (!v) return "—";

  const labels = {
    draft: "Draft",
    submitted: "Submitted",
    approved: "Approved",
    sent: "Sent",
    accepted: "Accepted",
    rejected: "Rejected",
    expired: "Expired",
    withdrawn: "Withdrawn",
    cancelled: "Cancelled",
    canceled: "Cancelled",
  };

  return labels[v] || v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, " ");
};

const transitionDescription = (status) => {
  const s = normStatus(status);

  const copy = {
    draft: "Return quote to Draft so it can be amended.",
    submitted: "Move quote to Submitted.",
    approved: "Approve quote and allow it to be sent.",
    sent: "Mark quote as Sent.",
    accepted: "Mark quote as Accepted by the customer.",
    rejected: "Mark quote as Rejected.",
    expired: "Mark quote as Expired.",
    withdrawn: "Mark quote as Withdrawn after customer/order change.",
    cancelled: "Cancel this quote.",
    canceled: "Cancel this quote.",
  };

  return copy[s] || `Move quote to ${fmtStatus(s)}.`;
};

const pillStyleForStatus = (status, theme) => {
  const s = normStatus(status);

  const base = {
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#374151",
  };

  if (s === "draft") return base;
  if (s === "submitted") return { border: "1px solid #fdba74", background: "#fff7ed", color: "#9a3412" };
  if (s === "approved") return { border: `1px solid ${theme?.accent_color || "#86efac"}`, background: "#f0fdf4", color: "#166534" };
  if (s === "sent") return { border: `1px solid ${theme?.primary_color || "#93c5fd"}`, background: "#eff6ff", color: "#1d4ed8" };
  if (s === "accepted") return { border: "1px solid #86efac", background: "#f0fdf4", color: "#166534" };
  if (s === "withdrawn") return { border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e" };
  if (s === "rejected") return { border: "1px solid #fca5a5", background: "#fef2f2", color: "#991b1b" };
  if (s === "expired") return { border: "1px solid #fca5a5", background: "#fff1f2", color: "#9f1239" };
  if (s === "cancelled" || s === "canceled") return { border: "1px solid #d1d5db", background: "#f3f4f6", color: "#374151" };

  return base;
};

const extractErrorMessage = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return e?.message || fallback;
};

const optionLabel = (o) => {
  const key = norm(o?.option_key || o?.optionKey);
  const label = norm(o?.label) || norm(o?.name) || norm(o?.title);
  if (label) return label;
  if (key) return `Option ${key}`;
  return `Option #${o?.id ?? "?"}`;
};

const getQuoteOptionsFromMeta = (meta) => {
  const direct =
    safeArray(meta?.quote_options).length ? meta.quote_options :
    safeArray(meta?.options).length ? meta.options :
    safeArray(meta?.quote?.options).length ? meta.quote.options :
    [];

  return safeArray(direct)
    .map((o) => {
      const id = toIntOrNull(o?.id);
      if (!id) return null;
      return { ...o, id, label: optionLabel(o) };
    })
    .filter(Boolean);
};

// Quote → Job helpers ---------------------------------------------------------
// Pass quote line-item context to the global Job flyout without assuming that
// quote service_id values are Job Type ids. The flyout owns Job Type matching.
const buildJobTypeHint = (quoteTitle, items) => {
  const parts = [];

  if (norm(quoteTitle)) parts.push(norm(quoteTitle));

  for (const li of safeArray(items)) {
    const text =
      norm(li?.name) ||
      norm(li?.description) ||
      norm(li?.service_name) ||
      norm(li?.service?.name);

    if (text) parts.push(text);
  }

  return [...new Set(parts)].join(" | ");
};

const panelStyle = {
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
};

export default function NextStepCard({
  quoteId,
  meta: metaProp = null,
  autoLoad = true,
  onReloadMeta,
  onQuoteChanged,
  quoteTitle = "",
  customerId = null,
  opportunityId = null,
  lineItems = [],
  canEditOverride = null,
  onTransition,
  onCreateJob,
  approverOptions = [],
  defaultApproverId = "",
  onSubmit,
  onApprove,
  onReject,
  onSend,
  onSendEmail,
  emailJobId = null,
  emailJobRef = "",
  emailDefaultTo = "",
  emailContactId = null,
  emailContactEmail = "",
  emailFromAddress = "",
  emailFromDisplayName = "",
  emailLocation = "",
  contactId = null,
  contactEmail = "",
  defaultTo = "",
  onBackToDraft,
  compact = false,
  onAlert,
}) {
  const themeCtx = useTheme?.() || null;
  const theme = themeCtx?.theme || {};

  const [meta, setMeta] = useState(metaProp);
  const [busy, setBusy] = useState(false);
  const [transitionBusy, setTransitionBusy] = useState("");
  const [err, setErr] = useState("");
  const [approverId, setApproverId] = useState(String(defaultApproverId || ""));
  const [rejectReason, setRejectReason] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDefaultAttachments, setEmailDefaultAttachments] = useState([]);
  const [emailPrepBusy, setEmailPrepBusy] = useState(false);

  useEffect(() => {
    setMeta(metaProp);
  }, [metaProp]);

  const qid = useMemo(() => toIntOrNull(quoteId), [quoteId]);

  const resolvedEmailContactId = useMemo(() => {
    const primary = toIntOrNull(emailContactId);
    if (primary) return primary;
    return toIntOrNull(contactId);
  }, [emailContactId, contactId]);

  const resolvedEmailContactEmail = useMemo(() => {
    const primary = norm(emailContactEmail);
    if (primary) return primary;
    return norm(contactEmail);
  }, [emailContactEmail, contactEmail]);

  const resolvedEmailDefaultTo = useMemo(() => {
    const primary = norm(emailDefaultTo);
    if (primary) return primary;
    const alias = norm(defaultTo);
    if (alias) return alias;
    return resolvedEmailContactEmail;
  }, [emailDefaultTo, defaultTo, resolvedEmailContactEmail]);

  const loadMeta = async () => {
    if (!qid) return;
    setBusy(true);
    setErr("");
    try {
      const res = await axiosInstance.get(`/api/quotes/${qid}/status/meta`);
      setMeta(res?.data || null);
    } catch (e) {
      const msg = extractErrorMessage(e, "Failed to load status meta.");
      setErr(String(msg));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!autoLoad) return;
    if (!qid) return;
    if (metaProp) return;
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, qid]);

  const status = normStatus(meta?.status);
  const allowed = safeArray(meta?.allowed_next_statuses).map((x) => normStatus(x)).filter(Boolean);
  const canEdit = canEditOverride != null ? !!canEditOverride : !!meta?.can_edit_quote;
  const isAdmin = !!meta?.is_admin;

  const multiOptionEnabled = !!(
    meta?.multi_option_enabled ??
    meta?.multiOptionEnabled ??
    meta?.quote?.multi_option_enabled ??
    meta?.quote?.multiOptionEnabled
  );

  const acceptedQuoteOptionId = toIntOrNull(
    meta?.accepted_quote_option_id ??
      meta?.acceptedQuoteOptionId ??
      meta?.quote?.accepted_quote_option_id ??
      meta?.quote?.acceptedQuoteOptionId
  );

  const quoteOptions = useMemo(() => getQuoteOptionsFromMeta(meta), [meta]);

  const acceptedOption = useMemo(() => {
    if (!acceptedQuoteOptionId) return null;
    return quoteOptions.find((o) => Number(o.id) === Number(acceptedQuoteOptionId)) || null;
  }, [acceptedQuoteOptionId, quoteOptions]);

  const btnGeneral = "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white";
  const btnAction = "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white";
  const header = compact ? "Next step" : "Next step (workflow)";

  const showSentCommercialActions = status === "sent" && canEdit;
  const showAcceptedCommercialActions = status === "accepted" && canEdit;

  const adminStageDestinations = isAdmin
    ? ADMIN_STAGE_DESTINATIONS.filter((target) => target !== status)
    : [];

  const showAdminStageControl =
    isAdmin &&
    canEdit &&
    adminStageDestinations.length > 0 &&
    !showSentCommercialActions &&
    !showAcceptedCommercialActions;

  const showDraftSubmit =
    !showSentCommercialActions &&
    !showAcceptedCommercialActions &&
    status === "draft" &&
    hasAllowed(meta, "submitted");

  const showSubmittedDecide =
    !showSentCommercialActions &&
    !showAcceptedCommercialActions &&
    status === "submitted" &&
    (hasAllowed(meta, "approved") || hasAllowed(meta, "rejected"));

  const showApprovedSend =
    !showSentCommercialActions &&
    !showAcceptedCommercialActions &&
    status === "approved" &&
    hasAllowed(meta, "sent");

  const showRejectedBack =
    !showSentCommercialActions &&
    !showAcceptedCommercialActions &&
    status === "rejected";

  const showExpiredRes =
    isAdmin &&
    canEdit &&
    !showAdminStageControl &&
    !showSentCommercialActions &&
    !showAcceptedCommercialActions &&
    status === "expired";

  const approverOpts = useMemo(() => {
    const src = safeArray(approverOptions);
    return src
      .map((o) => {
        const id = toIntOrNull(o?.id);
        if (!id) return null;
        const label = norm(o?.label) || norm(o?.name) || norm(o?.email) || (id ? `Resource #${id}` : "Approver");
        return { id, label };
      })
      .filter(Boolean);
  }, [approverOptions]);

  const callReload = async () => {
    try {
      if (typeof onReloadMeta === "function") await onReloadMeta();
      else if (!metaProp) await loadMeta();
    } catch {
      // parent owns errors
    }
  };

  const guard = (fn, msg) => {
    if (!qid) {
      onAlert?.("warning", "Create/save the quote first.");
      return null;
    }
    if (!canEdit) {
      onAlert?.("warning", msg || "No edit access to this quote.");
      return null;
    }
    if (typeof fn !== "function") {
      onAlert?.("warning", "Action is not wired yet (missing handler).");
      return null;
    }
    return fn;
  };

  const transitionQuoteStatus = async (nextStatus, extra = {}) => {
    const target = normStatus(nextStatus);
    if (!qid || !target) throw new Error("Missing quote id or status.");

    if (typeof onTransition === "function") {
      return onTransition({
        quoteId: qid,
        status: target,
        fromStatus: status,
        meta,
        ...extra,
      });
    }

    const payload = {
      status: target,
      to_status: target,
      next_status: target,
      ...extra,
    };

    const res = await axiosInstance.post(`/api/quotes/${qid}/status`, payload);
    return res?.data || null;
  };

  const doTransition = async (nextStatus, extra = {}) => {
    const target = normStatus(nextStatus);

    if (!qid) {
      onAlert?.("warning", "Create/save the quote first.");
      return;
    }

    if (!canEdit) {
      onAlert?.("warning", "No edit access to this quote.");
      return;
    }

    if (!allowed.includes(target)) {
      onAlert?.("warning", `${fmtStatus(target)} is not allowed by quote governance.`);
      return;
    }

    const acceptedOptionId = toIntOrNull(extra?.accepted_quote_option_id ?? extra?.acceptedQuoteOptionId);
    const busyKey = acceptedOptionId ? `${target}:${acceptedOptionId}` : target;

    setTransitionBusy(busyKey);
    setBusy(true);
    setErr("");

    try {
      await transitionQuoteStatus(target, extra);
      await callReload();
      if (typeof onQuoteChanged === "function") await onQuoteChanged();
      onAlert?.("success", `Quote moved to ${fmtStatus(target)}.`);
    } catch (e) {
      const msg = extractErrorMessage(e, `Failed to move quote to ${fmtStatus(target)}.`);
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setTransitionBusy("");
      setBusy(false);
    }
  };

  const doAdminTransition = async (nextStatus) => {
    const target = normStatus(nextStatus);

    if (!qid) {
      onAlert?.("warning", "Create/save the quote first.");
      return;
    }

    if (!canEdit) {
      onAlert?.("warning", "No edit access to this quote.");
      return;
    }

    if (!isAdmin) {
      onAlert?.("warning", "Administrator access is required for direct stage movement.");
      return;
    }

    if (!ADMIN_STAGE_DESTINATIONS.includes(target) || target === status) {
      onAlert?.("warning", "That administrative stage movement is not available.");
      return;
    }

    setTransitionBusy(target);
    setBusy(true);
    setErr("");

    try {
      await transitionQuoteStatus(target, { override: true });
      await callReload();
      if (typeof onQuoteChanged === "function") await onQuoteChanged();
      onAlert?.("success", `Quote moved directly to ${fmtStatus(target)}.`);
    } catch (e) {
      const msg = extractErrorMessage(e, `Failed to move quote directly to ${fmtStatus(target)}.`);
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setTransitionBusy("");
      setBusy(false);
    }
  };

  const doAcceptQuote = async () => doTransition("accepted");

  const doAcceptOption = async (optionId) => {
    const oid = toIntOrNull(optionId);
    if (!oid) {
      onAlert?.("warning", "Missing quote option id.");
      return;
    }

    await doTransition("accepted", {
      accepted_quote_option_id: oid,
      acceptedQuoteOptionId: oid,
    });
  };

  const doWithdraw = async () => doTransition("withdrawn");
  const doExpire = async () => doTransition("expired");

  const doCreateJob = async () => {
    if (!qid) {
      onAlert?.("warning", "Create/save the quote first.");
      return;
    }

    const launchPayload = {
      quote_id: qid,
      opportunity_id: toIntOrNull(
        opportunityId ??
          meta?.opportunity_id ??
          meta?.opportunityId ??
          meta?.quote?.opportunity_id ??
          meta?.quote?.opportunityId
      ),
      customer_id: toIntOrNull(
        customerId ??
          meta?.customer_id ??
          meta?.customerId ??
          meta?.quote?.customer_id ??
          meta?.quote?.customerId
      ),
      contact_id: toIntOrNull(
        contactId ??
          meta?.contact_id ??
          meta?.contactId ??
          meta?.quote?.contact_id ??
          meta?.quote?.contactId
      ),
      job_type_id: toIntOrNull(
        meta?.suggested_job_type_id ??
          meta?.suggestedJobTypeId ??
          meta?.job_type_id ??
          meta?.jobTypeId ??
          meta?.quote?.suggested_job_type_id ??
          meta?.quote?.job_type_id
      ),
      job_type_hint: buildJobTypeHint(quoteTitle, lineItems),
      quote_line_items: safeArray(lineItems),
      site_name: norm(quoteTitle),
      description: norm(quoteTitle) ? `Created from Quote #${qid} – ${norm(quoteTitle)}` : `Created from Quote #${qid}`,
      accepted_quote_option_id: acceptedQuoteOptionId,
      accepted_option: acceptedOption,
    };

    if (typeof onCreateJob === "function") {
      try {
        await onCreateJob({
          quoteId: qid,
          meta,
          ...launchPayload,
        });
      } catch (e) {
        const msg = extractErrorMessage(e, "Failed to launch job creation.");
        setErr(String(msg));
        onAlert?.("critical", String(msg));
      }
      return;
    }

    try {
      if (typeof window.__GJCF_PREFILL !== "function") {
        throw new Error("Global Job Create flyout is not available.");
      }
      window.__GJCF_PREFILL(launchPayload);
    } catch (e) {
      const msg = extractErrorMessage(e, "Failed to launch job creation.");
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    }
  };

  const doSubmit = async () => {
    const fn = guard(onSubmit, "No edit access to submit this quote.");
    if (!fn) return;

    const rid = toIntOrNull(approverId);
    if (!rid) {
      onAlert?.("warning", "Select an approver.");
      return;
    }

    setBusy(true);
    setErr("");
    try {
      await fn({ quoteId: qid, approver_resource_id: rid });
      await callReload();
      onAlert?.("success", "Submitted for approval.");
    } catch (e) {
      const msg = extractErrorMessage(e, "Submit failed.");
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setBusy(false);
    }
  };

  const doApprove = async () => {
    const fn = guard(onApprove, "No edit access to approve this quote.");
    if (!fn) return;

    setBusy(true);
    setErr("");
    try {
      await fn({ quoteId: qid });
      await callReload();
      onAlert?.("success", "Approved.");
    } catch (e) {
      const msg = extractErrorMessage(e, "Approve failed.");
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    const fn = guard(onReject, "No edit access to reject this quote.");
    if (!fn) return;

    const r = norm(rejectReason);
    if (!r) {
      onAlert?.("warning", "Reason is required for rejection.");
      return;
    }

    setBusy(true);
    setErr("");
    try {
      await fn({ quoteId: qid, reason: r });
      await callReload();
      onAlert?.("success", "Rejected.");
    } catch (e) {
      const msg = extractErrorMessage(e, "Reject failed.");
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setBusy(false);
    }
  };

  const doLaunchSend = async () => {
    if (!qid) {
      onAlert?.("warning", "Create/save the quote first.");
      return;
    }
    if (!canEdit) {
      onAlert?.("warning", "No edit access to send this quote.");
      return;
    }

    setEmailPrepBusy(true);
    setErr("");

    try {
      const res = await axiosInstance.get(`/api/quotes/${qid}/pdf/preview`, { responseType: "blob" });
      const blob = res?.data;

      if (blob) {
        const file = new File([blob], `quote_${qid}.pdf`, { type: "application/pdf" });
        setEmailDefaultAttachments([file]);
      } else {
        setEmailDefaultAttachments([]);
      }
    } catch (e) {
      setEmailDefaultAttachments([]);
      const msg = extractErrorMessage(e, "Failed to load PDF preview for attachment.");
      onAlert?.("warning", `Email composer opened without PDF attachment: ${String(msg)}`);
    } finally {
      setEmailPrepBusy(false);
      setEmailOpen(true);
    }
  };

  const doBackToDraft = async () => {
    const fn = guard(onBackToDraft, "No edit access to move this quote back to draft.");
    if (!fn) return;

    setBusy(true);
    setErr("");
    try {
      await fn({ quoteId: qid });
      await callReload();
      onAlert?.("success", "Moved back to Draft.");
    } catch (e) {
      const msg = extractErrorMessage(e, "Action failed.");
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setBusy(false);
    }
  };

  const doResurrect = async () => {
    if (!qid) {
      onAlert?.("warning", "Create/save the quote first.");
      return;
    }

    if (!canEdit) {
      onAlert?.("warning", "No edit access to resurrect this quote.");
      return;
    }

    if (!isAdmin) {
      onAlert?.("warning", "Administrator access is required to resurrect an expired quote.");
      return;
    }

    setTransitionBusy("draft");
    setBusy(true);
    setErr("");
    try {
      await transitionQuoteStatus("draft", { override: true });
      await callReload();
      if (typeof onQuoteChanged === "function") await onQuoteChanged();
      onAlert?.("success", "Expired quote reopened as Draft.");
    } catch (e) {
      const msg = extractErrorMessage(e, "Resurrect failed.");
      setErr(String(msg));
      onAlert?.("critical", String(msg));
    } finally {
      setTransitionBusy("");
      setBusy(false);
    }
  };

  const noActions =
    !showAdminStageControl &&
    !showSentCommercialActions &&
    !showAcceptedCommercialActions &&
    !showDraftSubmit &&
    !showSubmittedDecide &&
    !showApprovedSend &&
    !showRejectedBack &&
    !showExpiredRes;

  const emailActionLabel = useMemo(() => {
    const t = norm(quoteTitle);
    if (qid && t) return `Quote #${qid} – ${t}`;
    if (qid) return `Quote #${qid}`;
    return "Quote";
  }, [qid, quoteTitle]);

  return (
    <>
      <div
        className="min-w-0"
        style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}
      >
        <div
          className="flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between"
          style={{
            padding: compact ? 12 : 14,
            borderBottom: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <div className="min-w-0 flex-1">
            <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>{header}</div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              Status is governed by DB. UI is driven by{" "}
              <span style={{ fontFamily: "ui-monospace" }}>/status/meta</span>.
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
            <span
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 900,
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                ...pillStyleForStatus(status, theme),
              }}
              title="Current quote status"
            >
              {fmtStatus(status)}
            </span>

            <button
              type="button"
              className={btnGeneral}
              style={{
                borderRadius: 999,
                padding: "6px 10px",
                fontWeight: 900,
                fontSize: 11,
                opacity: busy ? 0.7 : 1,
              }}
              onClick={() => {
                if (typeof onReloadMeta === "function") onReloadMeta();
                else loadMeta();
              }}
              disabled={!qid || busy}
              title={!qid ? "Create/save the quote first" : "Reload status meta"}
            >
              {busy ? "…" : "Reload"}
            </button>
          </div>
        </div>

        <div style={{ padding: compact ? 12 : 14 }}>
          {err ? (
            <div
              className="break-words"
              style={{ marginBottom: 10, border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: 10, padding: 10, fontSize: 12 }}
            >
              {err}
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 10 }}>
            <div
              className="min-w-0"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(78px, 110px) minmax(0, 1fr)",
                gap: 8,
                fontSize: 11,
                color: "#374151",
              }}
            >
              <div style={{ color: "#6b7280" }}>Quote</div>
              <div style={{ fontFamily: "ui-monospace" }}>{qid ? `#${qid}` : "—"}</div>

              <div style={{ color: "#6b7280" }}>Title</div>
              <div style={{ fontFamily: "ui-monospace", minWidth: 0, overflowWrap: "anywhere" }}>
                {norm(quoteTitle) ? quoteTitle : "—"}
              </div>

              <div style={{ color: "#6b7280" }}>Allowed next</div>
              <div style={{ fontFamily: "ui-monospace", minWidth: 0, overflowWrap: "anywhere" }}>{allowed.length ? allowed.join(", ") : "—"}</div>

              <div style={{ color: "#6b7280" }}>Access</div>
              <div style={{ fontFamily: "ui-monospace", minWidth: 0, overflowWrap: "anywhere" }}>
                can_edit={String(!!canEdit)} • admin={String(!!isAdmin)}
              </div>

              {multiOptionEnabled ? (
                <>
                  <div style={{ color: "#6b7280" }}>Quote type</div>
                  <div style={{ fontFamily: "ui-monospace", minWidth: 0, overflowWrap: "anywhere" }}>
                    multi-option • accepted option={acceptedOption ? acceptedOption.label : acceptedQuoteOptionId || "—"}
                  </div>
                </>
              ) : null}
            </div>

            {showSentCommercialActions ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Customer Response</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Record the real-world customer outcome. Acceptance confirms the quote/order is proceeding.
                </div>

                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  {multiOptionEnabled ? (
                    quoteOptions.length ? (
                      quoteOptions.map((opt) => {
                        const busyKey = `accepted:${opt.id}`;
                        const isBusy = transitionBusy === busyKey;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => doAcceptOption(opt.id)}
                            disabled={!qid || !canEdit || busy || !allowed.includes("accepted")}
                            className={btnAction}
                            style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy || !allowed.includes("accepted") ? 0.65 : 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, textAlign: "left", flexWrap: "wrap", overflowWrap: "anywhere" }}
                            title={`Accept ${opt.label}`}
                          >
                            <span>{isBusy ? "Accepting..." : `Accept ${opt.label}`}</span>
                            <span style={{ fontSize: 10, opacity: 0.85, fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}>Selects this option and marks quote Accepted</span>
                          </button>
                        );
                      })
                    ) : (
                      <div style={{ border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 700 }}>
                        Multi-option quote has no option list in status meta yet. Backend must return quote_options/options.
                      </div>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={doAcceptQuote}
                      disabled={!qid || !canEdit || busy || !allowed.includes("accepted")}
                      className={btnAction}
                      style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy || !allowed.includes("accepted") ? 0.65 : 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, textAlign: "left", flexWrap: "wrap", overflowWrap: "anywhere" }}
                      title="Customer accepted the quote"
                    >
                      <span>{transitionBusy === "accepted" ? "Accepting..." : "Accept Quote"}</span>
                      <span style={{ fontSize: 10, opacity: 0.85, fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}>Customer accepted</span>
                    </button>
                  )}

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={doWithdraw}
                      disabled={!qid || !canEdit || busy || !allowed.includes("withdrawn")}
                      className={btnGeneral}
                      style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy || !allowed.includes("withdrawn") ? 0.65 : 1 }}
                      title="Customer changed their mind or pulled the order"
                    >
                      {transitionBusy === "withdrawn" ? "Withdrawing..." : "Withdrawn"}
                    </button>

                    <button
                      type="button"
                      onClick={doExpire}
                      disabled={!qid || !canEdit || busy || !allowed.includes("expired")}
                      className={btnGeneral}
                      style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy || !allowed.includes("expired") ? 0.65 : 1 }}
                      title="Quote expired without customer acceptance"
                    >
                      {transitionBusy === "expired" ? "Expiring..." : "Expired"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {showAcceptedCommercialActions ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Accepted Quote</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Next operational step is to create the job. Withdraw remains available if the customer has a change of heart.
                </div>

                {acceptedOption ? (
                  <div style={{ marginTop: 10, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 800 }}>
                    Accepted option: {acceptedOption.label}
                  </div>
                ) : null}

                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  <button
                    type="button"
                    onClick={doCreateJob}
                    disabled={!qid || busy}
                    className={btnAction}
                    style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || busy ? 0.65 : 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, textAlign: "left", flexWrap: "wrap", overflowWrap: "anywhere" }}
                    title="Create job from accepted quote"
                  >
                    <span>Create Job</span>
                    <span style={{ fontSize: 10, opacity: 0.85, fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}>Launch job creation prefilled from quote</span>
                  </button>

                  <button
                    type="button"
                    onClick={doWithdraw}
                    disabled={!qid || !canEdit || busy || !allowed.includes("withdrawn")}
                    className={btnGeneral}
                    style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy || !allowed.includes("withdrawn") ? 0.65 : 1 }}
                    title="Customer changed their mind after acceptance"
                  >
                    {transitionBusy === "withdrawn" ? "Withdrawing..." : "Withdrawn"}
                  </button>
                </div>
              </div>
            ) : null}

            {showAdminStageControl ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Move Quote Stage</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Administrative stage override. Move directly to another quote stage without stepping through the normal workflow.
                </div>

                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                  {adminStageDestinations.map((target) => {
                    const isBusy = transitionBusy === target;

                    return (
                      <button
                        key={target}
                        type="button"
                        onClick={() => doAdminTransition(target)}
                        disabled={!qid || !canEdit || busy}
                        className={target === "cancelled" || target === "canceled" || target === "rejected" || target === "withdrawn" ? btnGeneral : btnAction}
                        style={{ borderRadius: 12, padding: "9px 12px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy ? 0.65 : 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, textAlign: "left", flexWrap: "wrap", overflowWrap: "anywhere" }}
                        title={transitionDescription(target)}
                      >
                        <span>{isBusy ? "Moving..." : `Move to ${fmtStatus(target)}`}</span>
                        <span style={{ fontSize: 10, opacity: 0.85, fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}>{transitionDescription(target)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {showDraftSubmit ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Submit for approval</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Select an approver and submit. Backend will handle notification + governed status transition.
                </div>

                <div className="mt-2.5 grid grid-cols-1 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>Approver</div>
                    <select
                      value={approverId}
                      onChange={(e) => setApproverId(e.target.value)}
                      disabled={!canEdit || busy}
                      style={{ width: "100%", minWidth: 0, border: "1px solid #d1d5db", borderRadius: 10, padding: "8px 10px", fontSize: 12, background: !canEdit ? "#f3f4f6" : "#fff" }}
                    >
                      <option value="">{approverOpts.length ? "Select approver…" : "No approvers provided (wire approverOptions)"}</option>
                      {approverOpts.map((o) => (
                        <option key={o.id} value={String(o.id)}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className={`${btnAction} sm:w-auto`}
                    onClick={doSubmit}
                    disabled={!qid || !canEdit || busy}
                    style={{ borderRadius: 999, padding: "8px 14px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy ? 0.6 : 1, width: "100%" }}
                    title={!canEdit ? "No edit access" : "Submit for approval"}
                  >
                    Submit
                  </button>
                </div>
              </div>
            ) : null}

            {showSubmittedDecide ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Decision</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>Approve or reject. Reject requires a reason.</div>

                <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                  <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                    <button
                      type="button"
                      className={`${btnAction} w-full sm:w-auto`}
                      onClick={doApprove}
                      disabled={!qid || !canEdit || busy || !hasAllowed(meta, "approved")}
                      style={{ borderRadius: 999, padding: "8px 14px", fontWeight: 900, fontSize: 12, opacity: !hasAllowed(meta, "approved") ? 0.5 : 1 }}
                      title={!hasAllowed(meta, "approved") ? "Not allowed by governance" : "Approve"}
                    >
                      Approve
                    </button>

                    <button
                      type="button"
                      className={`${btnGeneral} w-full sm:w-auto`}
                      onClick={doReject}
                      disabled={!qid || !canEdit || busy || !hasAllowed(meta, "rejected")}
                      style={{ borderRadius: 999, padding: "8px 14px", fontWeight: 900, fontSize: 12, opacity: !hasAllowed(meta, "rejected") ? 0.5 : 1 }}
                      title={!hasAllowed(meta, "rejected") ? "Not allowed by governance" : "Reject"}
                    >
                      Reject
                    </button>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>Rejection reason</div>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      disabled={!canEdit || busy}
                      rows={compact ? 2 : 3}
                      style={{ width: "100%", minWidth: 0, border: "1px solid #d1d5db", borderRadius: 10, padding: "8px 10px", fontSize: 12, resize: "vertical", background: !canEdit ? "#f3f4f6" : "#fff" }}
                      placeholder="Required if rejecting…"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {showApprovedSend ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Send quote</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Launches the email composer and pre-attaches the current Quote PDF preview.
                </div>

                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={btnAction}
                    onClick={doLaunchSend}
                    disabled={!qid || !canEdit || busy || emailPrepBusy}
                    style={{ borderRadius: 999, padding: "8px 14px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || busy || emailPrepBusy ? 0.7 : 1 }}
                    title={!canEdit ? "No edit access" : "Launch email composer"}
                  >
                    {emailPrepBusy ? "Preparing…" : "Launch Send"}
                  </button>
                </div>
              </div>
            ) : null}

            {showRejectedBack ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Rejected</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  If you want to amend, move it back to <b>draft</b>, edit, then resubmit.
                </div>

                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={btnGeneral}
                    onClick={doBackToDraft}
                    disabled={!qid || !canEdit || busy || typeof onBackToDraft !== "function"}
                    style={{ borderRadius: 999, padding: "8px 14px", fontWeight: 900, fontSize: 12, opacity: typeof onBackToDraft !== "function" ? 0.5 : 1 }}
                    title={typeof onBackToDraft !== "function" ? "Handler not wired yet" : "Move back to draft"}
                  >
                    Back to Draft
                  </button>
                </div>
              </div>
            ) : null}

            {showExpiredRes ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>Expired</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Reopen this expired quote as Draft. It must then pass through the full approval workflow again.
                </div>

                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={btnAction}
                    onClick={doResurrect}
                    disabled={!qid || !canEdit || !isAdmin || busy}
                    style={{ borderRadius: 999, padding: "8px 14px", fontWeight: 900, fontSize: 12, opacity: !qid || !canEdit || !isAdmin || busy ? 0.65 : 1 }}
                    title="Administrator override: reopen expired quote as Draft"
                  >
                    {transitionBusy === "draft" ? "Reopening..." : "Resurrect to Draft"}
                  </button>
                </div>
              </div>
            ) : null}

            {noActions ? (
              <div style={panelStyle}>
                <div style={{ fontWeight: 900, fontSize: 12, color: "#111827" }}>No next action available</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  This can happen if meta is missing, governance returns no allowed transitions, or the quote is in a terminal state.
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <EmailMessageModal
        open={emailOpen}
        onClose={async () => {
          setEmailOpen(false);
          setEmailDefaultAttachments([]);
          await callReload();
        }}
        jobId={emailJobId}
        jobRef={emailJobRef}
        defaultTo={resolvedEmailDefaultTo}
        location={emailLocation}
        actionLabel={emailActionLabel}
        fromAddress={emailFromAddress}
        fromDisplayName={emailFromDisplayName}
        contactId={resolvedEmailContactId}
        contactEmail={resolvedEmailContactEmail}
        initialAttachments={emailDefaultAttachments}
        onSend={onSendEmail}
      />
    </>
  )
}